import React, { useState, useEffect } from 'react';
import { Search, MessageSquare, Users, Settings, Plus } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import NotificationBadge from './NotificationBadge';
import ThemeToggle from './ThemeToggle';
import { ChatListSkeleton } from '../Skeletons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatSidebar = ({ onSelectConversation, onStartNewChat }) => {
  const { conversations, activeConversation, unreadCount, fetchConversations, isConnected } = useChat();
  const { user, token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);

  // Fetch friends for new chat dialog
  const fetchFriends = async () => {
    if (!token) return;
    setLoadingFriends(true);
    try {
      const response = await axios.get(`${API_URL}/api/social/following/${user?.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(response.data?.following || []);
    } catch (error) {
      console.error('Error fetching friends:', error);
    } finally {
      setLoadingFriends(false);
    }
  };

  useEffect(() => {
    if (showNewChat) {
      fetchFriends();
    }
  }, [showNewChat]);

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv => {
    const otherParticipant = conv.participants?.find(p => p !== user?.id);
    const participantName = conv.participant_info?.display_name || conv.participant_info?.username || 'User';
    return participantName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatLastMessage = (message) => {
    if (!message) return 'No messages yet';
    if (message.voice_url) return '🎤 Voice message';
    return message.content?.substring(0, 30) + (message.content?.length > 30 ? '...' : '');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="w-80 bg-gray-900 border-r border-gray-800 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-blue-500" />
            <h2 className="text-lg font-bold text-white">Messages</h2>
            <NotificationBadge count={unreadCount} />
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} 
                 title={isConnected ? 'Connected' : 'Disconnected'} />
            <ThemeToggle />
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* New Chat Button */}
      <div className="p-3 border-b border-gray-800">
        <button
          onClick={() => setShowNewChat(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />
          <span>New Message</span>
        </button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
            <p className="text-sm">No conversations yet</p>
            <p className="text-xs">Start a new message!</p>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const isActive = activeConversation?.id === conversation.id;
            const participantName = conversation.participant_info?.display_name || 
                                   conversation.participant_info?.username || 'User';
            const hasUnread = conversation.unread_count > 0;

            return (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation)}
                className={`w-full p-3 flex items-center gap-3 hover:bg-gray-800 transition-colors text-left ${
                  isActive ? 'bg-gray-800 border-l-2 border-blue-500' : ''
                }`}
              >
                {/* Avatar */}
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                    {participantName.charAt(0).toUpperCase()}
                  </div>
                  {conversation.participant_info?.is_online && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-gray-900 rounded-full"></span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className={`font-medium truncate ${hasUnread ? 'text-white' : 'text-gray-300'}`}>
                      {participantName}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTime(conversation.updated_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className={`text-sm truncate ${hasUnread ? 'text-gray-300' : 'text-gray-500'}`}>
                      {formatLastMessage(conversation.last_message)}
                    </p>
                    {hasUnread && <NotificationBadge count={conversation.unread_count} className="ml-2" />}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-gray-900 rounded-lg w-96 max-h-[70vh] overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Start New Chat</h3>
                <button onClick={() => setShowNewChat(false)} className="text-gray-400 hover:text-white">
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-4 max-h-96 overflow-y-auto">
              {loadingFriends ? (
                <div className="text-center text-gray-500 py-4">Loading...</div>
              ) : friends.length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Follow some users to start chatting!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {friends.map((friend) => (
                    <button
                      key={friend.id}
                      onClick={() => {
                        onStartNewChat(friend);
                        setShowNewChat(false);
                      }}
                      className="w-full p-3 flex items-center gap-3 hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {(friend.display_name || friend.username || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white font-medium">{friend.display_name || friend.username}</p>
                        <p className="text-sm text-gray-500">@{friend.username}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatSidebar;
